import { z } from 'zod';
/**
 * Integrations: a website's leads sent on to the customer's other tools
 * (`features/integrations`). Webhooks first; Slack and CRMs reuse the same
 * events.
 */
/**
 * What a webhook or Slack channel can be told about. `conversation.started`:
 * a visitor sent their first message to the assistant (opening the widget
 * alone isn't a conversation). `lead.created`: a conversation produced its
 * lead (a tap, or the lead form). `lead.updated`: the visitor sent the lead
 * form after a tap, or sent it again, so the lead's details changed
 * (`product/business-rules.md`, Leads).
 */
export const WEBHOOK_EVENT_TYPES = [
    'conversation.started',
    'lead.created',
    'lead.updated',
];
export const webhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);
export const WEBHOOK_EVENT_LABELS = {
    'conversation.started': 'New conversation',
    'lead.created': 'New lead',
    'lead.updated': 'Lead details changed',
};
/** The test event the dashboard sends; never stored or retried. */
export const WEBHOOK_TEST_EVENT_TYPE = 'webhook.test';
/**
 * `pending`: waiting for its next attempt. `succeeded`: the endpoint answered
 * 2xx. `failed`: every attempt failed. `skipped`: not sent, because the
 * endpoint was turned off or the website's plan no longer has integrations.
 */
export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'succeeded', 'failed', 'skipped'];
export const webhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);
/**
 * Why Groundly turned an endpoint off by itself. `failing`: it kept failing.
 * `revoked`: Slack says the connection is gone (the app was removed, or the
 * channel archived or deleted), so it can't work until it's connected again.
 */
export const WEBHOOK_DISABLED_REASONS = ['failing', 'revoked'];
export const webhookDisabledReasonSchema = z.enum(WEBHOOK_DISABLED_REASONS);
/** Lead events: what a new endpoint sends unless the customer also picks conversations. */
export const LEAD_EVENT_TYPES = ['lead.created', 'lead.updated'];
/** Endpoints a website may have at once. */
export const MAX_WEBHOOK_ENDPOINTS = 2;
/**
 * Where an endpoint sends: a `webhook` posts signed JSON to an address the
 * customer typed; `slack` posts a message to a Slack channel, through the
 * address Slack gave when the customer added Groundly to it.
 */
export const INTEGRATION_KINDS = ['webhook', 'slack', 'hubspot', 'salesforce', 'zoho'];
/** The CRM kinds: integrations that sync leads to a customer's CRM. */
export const CRM_KINDS = ['hubspot', 'salesforce', 'zoho'];
/** CRM connections a website may have per CRM at once. */
export const MAX_CRM_CONNECTIONS = 1;
/** What a new CRM connection syncs until the customer changes it: new leads only. */
export const DEFAULT_CRM_EVENTS = ['lead.created', 'lead.updated'];
/** Slack channels a website may post leads to at once. */
export const MAX_SLACK_CONNECTIONS = 2;
/** What a new Slack channel posts until the customer changes it: new leads only. */
export const DEFAULT_SLACK_EVENTS = ['lead.created'];
/**
 * When each attempt is made, in seconds after the event: at once, then
 * backing off to a day. An endpoint that's down for a day loses nothing.
 */
export const WEBHOOK_RETRY_SCHEDULE_SECONDS = [
    0,
    60,
    5 * 60,
    30 * 60,
    2 * 60 * 60,
    6 * 60 * 60,
    12 * 60 * 60,
    24 * 60 * 60,
];
/** Deliveries in a row that use up every attempt before the endpoint is turned off. */
export const WEBHOOK_DISABLE_AFTER_FAILED_DELIVERIES = 5;
/** The request headers a receiver checks (`features/integrations/webhooks.md`). */
export const WEBHOOK_HEADERS = {
    signature: 'Groundly-Signature',
    eventId: 'Groundly-Event-Id',
    eventType: 'Groundly-Event-Type',
    deliveryId: 'Groundly-Delivery-Id',
};
/** A receiver should refuse a signature older than this, against replays. */
export const WEBHOOK_SIGNATURE_TOLERANCE_SECONDS = 5 * 60;
export const WEBHOOK_MESSAGES = {
    urlInvalid: 'Enter the full address, starting with https://.',
    urlNotHttps: 'Webhook addresses must start with https://.',
    urlCredentials: 'Leave the user name and password out of the address.',
    eventsEmpty: 'Choose at least one event to send.',
};
/**
 * An endpoint's address: https only, no credentials in it. Whether it points
 * at a private network is checked by the backend when it connects.
 */
export const webhookUrlSchema = z
    .string()
    .trim()
    .max(2_000)
    .superRefine((value, ctx) => {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        ctx.addIssue({ code: 'custom', message: WEBHOOK_MESSAGES.urlInvalid });
        return;
    }
    if (url.protocol !== 'https:') {
        ctx.addIssue({ code: 'custom', message: WEBHOOK_MESSAGES.urlNotHttps });
        return;
    }
    if (url.username !== '' || url.password !== '') {
        ctx.addIssue({ code: 'custom', message: WEBHOOK_MESSAGES.urlCredentials });
    }
});
export const webhookEventsSchema = z
    .array(webhookEventTypeSchema)
    .min(1, WEBHOOK_MESSAGES.eventsEmpty)
    .transform((events) => [...new Set(events)]);
/**
 * The body of every webhook request. `data` is built when the request is
 * sent, so it is the lead and conversation as they are then. `lead` is null
 * for `conversation.started`, which comes before any lead; `messages` are the
 * conversation so far.
 */
export const webhookLeadPayloadSchema = z.object({
    /** The event's id, the same on every attempt: receivers dedupe on it. */
    id: z.string(),
    type: z.union([webhookEventTypeSchema, z.literal(WEBHOOK_TEST_EVENT_TYPE)]),
    createdAt: z.iso.datetime(),
    website: z.object({ id: z.uuid(), name: z.string() }),
    data: z.object({
        lead: z
            .object({
            id: z.string(),
            /** How the visitor became a lead: the form (`visitor_details`) or a tapped contact. */
            contactType: z.string(),
            name: z.string().nullable(),
            email: z.string().nullable(),
            phone: z.string().nullable(),
            message: z.string().nullable(),
            createdAt: z.iso.datetime(),
            updatedAt: z.iso.datetime(),
        })
            .nullable(),
        conversation: z.object({
            id: z.string(),
            startedAt: z.iso.datetime(),
            /** Opens the conversation in the Groundly dashboard. */
            url: z.string(),
            messages: z.array(z.object({
                role: z.string(),
                content: z.string(),
                createdAt: z.iso.datetime(),
            })),
        }),
    }),
});
//# sourceMappingURL=integrations.js.map